import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Terminal as TerminalIcon,
  Computer as ComputerIcon,
  CloudQueue as CloudIcon,
  PlayCircleOutline as PlayIcon,
} from '@mui/icons-material';

function EncoderSetupDialog({ open, onClose, encoder }) {
  const [copied, setCopied] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState('');
  // Pulled from system_settings.agent_backend_url so the URL shown here is
  // whatever the operator configured for encoders to talk to. Falls back to
  // the browser's origin if the setting hasn't been seeded yet.
  const [agentBackendUrl, setAgentBackendUrl] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/api/settings/agent_backend_url')
      .then(({ data }) => { if (!cancelled && data?.value) setAgentBackendUrl(data.value); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const getSetupCommand = () => {
    const serverUrl = agentBackendUrl || window.location.origin.replace(':3000', ':8000');
    return `sudo wget -O setup.sh ${serverUrl}/api/agent/setup/${encoder?.id} && sudo bash setup.sh --encoder-id ${encoder?.id} --server ${serverUrl} --name "${encoder?.name}"`;
  };

  const copyToClipboard = (text, commandName = 'main') => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          if (commandName === 'main') {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } else {
            setCopiedCommand(commandName);
            setTimeout(() => setCopiedCommand(''), 2000);
          }
        })
        .catch(() => {
          fallbackCopyToClipboard(text, commandName);
        });
    } else {
      fallbackCopyToClipboard(text, commandName);
    }
  };

  const fallbackCopyToClipboard = (text, commandName = 'main') => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      if (commandName === 'main') {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setCopiedCommand(commandName);
        setTimeout(() => setCopiedCommand(''), 2000);
      }
    } catch (_err) {
      /* swallow — best-effort */
    }

    document.body.removeChild(textArea);
  };

  if (!encoder) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <ComputerIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6">Setup Encoder: {encoder.name}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pb: 4 }}>
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 3 }} icon={<CloudIcon />}>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                WebSocket-based architecture
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Real-time bidirectional communication • No SSH keys • Auto-reconnect • Zero manual configuration
              </Typography>
            </Box>
          </Alert>

          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
              <PlayIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Quick Setup</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Run this single command on <strong>{encoder.hostname}</strong> to set up everything automatically:
            </Typography>

            <Paper elevation={2} sx={{ p: 2, mt: 2, backgroundColor: 'grey.900', border: 1, borderColor: 'grey.800' }}>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                }}>
                  <TerminalIcon sx={{ mr: 1.5, color: 'success.light', flexShrink: 0, mt: 0.5 }} />
                  <Box sx={{ flex: 1, mr: 1 }}>
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: '#00ff00',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        lineHeight: 1.6,
                      }}
                    >
                      {getSetupCommand()}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => copyToClipboard(getSetupCommand())}
                    size="small"
                    sx={{ 
                      flexShrink: 0,
                      backgroundColor: copied ? 'success.light' : 'action.hover',
                      '&:hover': {
                        backgroundColor: copied ? 'success.light' : 'action.selected',
                      }
                    }}
                  >
                    {copied ? <CheckIcon color="success" /> : <CopyIcon />}
                  </IconButton>
                </Box>
                {copied && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', ml: 5.5 }}>
                    ✓ Command copied to clipboard
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Installation Process
            </Typography>
            <Stepper orientation="vertical">
              <Step active>
                <StepLabel>System Preparation</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Downloads setup script and detects OS (Ubuntu/Debian/RHEL)
                  </Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>Dependencies Installation</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Installs Python 3, FFmpeg with full codec support, and multimedia tools
                  </Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>Agent Configuration</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Sets up encoder agent as a systemd service with auto-restart
                  </Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>Storage Setup</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Creates mount points: /mnt/source, /mnt/output, /mnt/overlay
                  </Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>Connection Establishment</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Establishes WebSocket connection and starts processing jobs
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Connection Status
            </Typography>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                border: 1, 
                borderColor: encoder.is_online ? 'success.main' : 'divider',
                backgroundColor: encoder.is_online ? 'success.lighter' : 'grey.50',
              }}
            >
              {encoder.is_online ? (
                <Box display="flex" alignItems="center">
                  <CheckIcon sx={{ color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="success.dark">
                      Encoder Connected
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ready to process watermarking jobs
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box display="flex" alignItems="center">
                  <CloudIcon sx={{ color: 'text.disabled', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle2">
                      Awaiting Connection
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Run the setup command above to connect this encoder
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Useful Commands
            </Typography>
            <Box sx={{ display: 'grid', gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Check service status:
                </Typography>
                <Paper elevation={2} sx={{ p: 2.5, backgroundColor: 'grey.900', border: 1, borderColor: 'grey.800' }}>
                  <Box display="flex" alignItems="center">
                    <TerminalIcon sx={{ mr: 1.5, color: 'success.light' }} />
                    <TextField
                      fullWidth
                      value="sudo systemctl status screeners-agent"
                      InputProps={{
                        readOnly: true,
                        style: { 
                          fontFamily: 'monospace', 
                          fontSize: '0.875rem',
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          padding: '8px 12px',
                          borderRadius: 1,
                          color: '#00ff00',
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => copyToClipboard('sudo systemctl status screeners-agent', 'status')}
                              size="small"
                              sx={{ 
                                ml: 1,
                                backgroundColor: copiedCommand === 'status' ? 'success.light' : 'action.hover',
                                '&:hover': {
                                  backgroundColor: copiedCommand === 'status' ? 'success.light' : 'action.selected',
                                }
                              }}
                            >
                              {copiedCommand === 'status' ? <CheckIcon color="success" /> : <CopyIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  View real-time logs:
                </Typography>
                <Paper elevation={2} sx={{ p: 2.5, backgroundColor: 'grey.900', border: 1, borderColor: 'grey.800' }}>
                  <Box display="flex" alignItems="center">
                    <TerminalIcon sx={{ mr: 1.5, color: 'success.light' }} />
                    <TextField
                      fullWidth
                      value="sudo journalctl -u screeners-agent -f"
                      InputProps={{
                        readOnly: true,
                        style: { 
                          fontFamily: 'monospace', 
                          fontSize: '0.875rem',
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          padding: '8px 12px',
                          borderRadius: 1,
                          color: '#00ff00',
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => copyToClipboard('sudo journalctl -u screeners-agent -f', 'logs')}
                              size="small"
                              sx={{ 
                                ml: 1,
                                backgroundColor: copiedCommand === 'logs' ? 'success.light' : 'action.hover',
                                '&:hover': {
                                  backgroundColor: copiedCommand === 'logs' ? 'success.light' : 'action.selected',
                                }
                              }}
                            >
                              {copiedCommand === 'logs' ? <CheckIcon color="success" /> : <CopyIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Restart service:
                </Typography>
                <Paper elevation={2} sx={{ p: 2.5, backgroundColor: 'grey.900', border: 1, borderColor: 'grey.800' }}>
                  <Box display="flex" alignItems="center">
                    <TerminalIcon sx={{ mr: 1.5, color: 'success.light' }} />
                    <TextField
                      fullWidth
                      value="sudo systemctl restart screeners-agent"
                      InputProps={{
                        readOnly: true,
                        style: { 
                          fontFamily: 'monospace', 
                          fontSize: '0.875rem',
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          padding: '8px 12px',
                          borderRadius: 1,
                          color: '#00ff00',
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => copyToClipboard('sudo systemctl restart screeners-agent', 'restart')}
                              size="small"
                              sx={{ 
                                ml: 1,
                                backgroundColor: copiedCommand === 'restart' ? 'success.light' : 'action.hover',
                                '&:hover': {
                                  backgroundColor: copiedCommand === 'restart' ? 'success.light' : 'action.selected',
                                }
                              }}
                            >
                              {copiedCommand === 'restart' ? <CheckIcon color="success" /> : <CopyIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default EncoderSetupDialog;